import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Globe, ChartBar, Sun, Moon } from "@phosphor-icons/react";
import { CALC } from "@/constants/testIds";
import { useT } from "@/lib/i18n";
import { UserMenu } from "@/components/UserMenu";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/ui/badge";

const getInitialTheme = () => {
  if (typeof window === "undefined") return "dark";
  const saved = localStorage.getItem("dpc_theme");
  if (saved === "dark" || saved === "light") return saved;
  return "dark"; // default = dark (dispatcher-friendly)
};

const applyTheme = (theme) => {
  const root = document.documentElement;
  if (theme === "dark") root.classList.add("dark");
  else root.classList.remove("dark");
};

export const AppHeader = ({ lang, setLang, guestUsage }) => {
  const t = useT(lang);
  const { isAuthenticated, isPro } = useAuth();
  const [theme, setTheme] = useState(getInitialTheme);

  useEffect(() => {
    applyTheme(theme);
    localStorage.setItem("dpc_theme", theme);
  }, [theme]);

  return (
    <header className="sticky top-0 z-50 bg-[#0A1128] text-white border-b border-black/40 relative overflow-hidden">
      <div className="absolute inset-0 top-bar-stripe pointer-events-none opacity-70" />
      {/* Neon line seam that connects the truck logo to the whole brand bar */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 h-px"
        style={{
          background:
            "linear-gradient(90deg, rgba(16,185,129,0) 0%, rgba(16,185,129,0.85) 8%, rgba(52,211,153,1) 22%, rgba(16,185,129,0.65) 60%, rgba(16,185,129,0) 100%)",
          boxShadow: "0 0 10px rgba(16,185,129,0.55), 0 0 22px rgba(16,185,129,0.35)",
        }}
      />
      <div className="relative max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 py-3 md:py-4 flex items-center justify-between gap-4">
        <Link
          to="/"
          className="flex items-center gap-3 group relative"
          aria-label="LoadWise home"
        >
          <img
            src="/branding/loadwise-logo-192.png"
            alt="LoadWise"
            data-testid={CALC.appTitle}
            className="h-11 md:h-12 w-auto select-none rounded-xl transition-transform group-hover:scale-[1.03] relative z-10"
            style={{ filter: "drop-shadow(0 0 14px rgba(16,185,129,0.5))" }}
            draggable="false"
          />
          <span className="sr-only">{t("appTitle")}</span>
          <p className="hidden lg:block text-[10px] uppercase tracking-[0.24em] text-white/70 leading-none font-medium">
            {t("appSubtitle")}
          </p>
        </Link>

        <div className="flex items-center gap-2 md:gap-3">
          {!isAuthenticated && guestUsage && guestUsage.limit && (
            <div
              data-testid="guest-usage-badge"
              className="hidden sm:flex items-center gap-2 text-[11px] uppercase tracking-[0.12em] text-white/60 me-2"
            >
              <span
                className={`w-1.5 h-1.5 rounded-full ${
                  guestUsage.remaining > 0 ? "bg-emerald-400" : "bg-red-400"
                } animate-pulse`}
              />
              {guestUsage.remaining}/{guestUsage.limit} free left
            </div>
          )}
          {isAuthenticated && (
            <Link
              to="/app/analytics"
              data-testid="header-analytics-link"
              className="hidden md:flex items-center gap-1.5 text-xs text-white/80 hover:text-white px-3 py-2 rounded-lg border border-white/10 hover:border-white/25"
            >
              <ChartBar size={14} weight="bold" />
              Analytics
            </Link>
          )}
          <button
            data-testid={CALC.langToggle}
            onClick={() => setLang(lang === "en" ? "ar" : "en")}
            className="flex items-center gap-1.5 rounded-lg border border-white/20 bg-white/5 hover:bg-white/10 px-2.5 md:px-3 py-2 text-xs md:text-sm font-medium transition-colors"
          >
            <Globe size={14} weight="bold" />
            {t("langToggle")}
          </button>
          <button
            data-testid="theme-toggle"
            aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            title={theme === "dark" ? "Light mode" : "Dark mode"}
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            className="flex items-center gap-1.5 rounded-lg border border-white/20 bg-white/5 hover:bg-white/10 px-2.5 py-2 text-xs md:text-sm font-medium transition-colors"
          >
            {theme === "dark" ? (
              <Sun size={14} weight="bold" />
            ) : (
              <Moon size={14} weight="bold" />
            )}
          </button>
          <UserMenu />
        </div>
      </div>
    </header>
  );
};
